import {inject, Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {INavigationCall, IServerApi, NavigationCall} from '@service-and-repairs/awpintegrationlib';
import {Subject} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {CustomerDataProfileService} from '../components/customer-data/customer-data-profile.service';
import {AnalyticsService} from '../core/analytics/services/analytics.service';
import {ConfigurationLoader} from '../core/configuration/services/configuration.loader';
import {ServiceCaseManager} from '../core/service-case/models/service-case-manager';
import {UserData} from '../core/user/models/user-data';
import {UserService} from '../core/user/services/user.service';
import {IIframeAppConfiguration} from '../interfaces/IIframeAppConfiguration';
import {IWebComponentAppConfiguration} from '../interfaces/IWebComponentAppConfiguration';
import {AwpClientLibService} from './awp-client-lib.service';
import {RouteFactoryService} from './route-factory.service';
import {AppsService} from "../core/apps/services/apps.service";

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private readonly router: Router = inject(Router);
  private readonly routeFactory: RouteFactoryService = inject(RouteFactoryService);
  private readonly userService: UserService = inject(UserService);
  private readonly appsService: AppsService = inject(AppsService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly configurationLoader: ConfigurationLoader = inject(ConfigurationLoader);
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);
  private readonly serviceCaseManger: ServiceCaseManager = inject(ServiceCaseManager);
  private readonly customerDataProfileService: CustomerDataProfileService = inject(CustomerDataProfileService);

  private readonly awpLib: IServerApi;
  private readonly routesLoaded: Subject<void> = new Subject<void>();
  private previousRoute: string;

  // One-shot guard so an intermediate '/' navigation (used to force a route change) does not
  // trigger the default-app / post-login redirect logic on the resulting root NavigationEnd.
  // Used by navigateToApp (INC000111195634) and by the interim navigateForcingRouteChange
  // (ISPI-481001).
  private suppressDefaultAppOnRootOnce = false;

  constructor() {
    this.awpLib = this.awpClientLibService.serverApi;
    this.openDefaultAppIfNoPathIsProvided();
    this.setupNavigationAnalytics();
    this.setupVehicleReload();

    this.userService.user$.subscribe((user: UserData) => {
      if (user) {
        this.configurationLoader.configChanged.subscribe(() => {
          this.createNavigationRoutesForApps();
          this.router.initialNavigation();
        });
      }
    });

    this.awpLib.subscribeToNavigateTo((plainNavigationCall: object): void => {
      const navigationCall: INavigationCall = NavigationCall.constructFromPlainObject(plainNavigationCall);
      this.navigateToApp(navigationCall.getApp(), navigationCall.getPathParam(), navigationCall.getQueryParams());
    });

    // Opens a customer's profile in the Customer Data Frontend's profile web component. Consumers call the Integration
    // Lib's openCustomerProfile(accountNumber) unconditionally; whether the Customer Data Frontend is available for the
    // current user can only be evaluated here, so an unavailable Customer Data Frontend is a safe no-op.
    this.awpLib.subscribeToOpenCustomerProfile((accountNumber: string): void => {
      this.customerDataProfileService.open(accountNumber);
    });
  }

  private navigateToApp(app: string, pathParam: string, queryParams: object): void {
    const params = {pathParam, queryParams};
    // Navigate to root first to trigger route change even if navigating to the same route.
    // Otherwise, params will not be updated if the app is already active.
    //
    // INC000111195634: suppress the default-app / post-login-redirect logic for this intermediate
    // '/' navigation. Without this guard the root NavigationEnd triggers
    // navigateToPostLoginRedirectOrDefaultApp, issuing a competing navigation that races with (and
    // can pre-empt) the intended navigation below. When that competing navigation targets the same
    // app, the routed WebComponentComponent is not reconstructed for the intended navigation, so its
    // router state (e.g. queryParams { appointment: 'true' }) is never read and the corresponding
    // web component attribute is lost.
    this.suppressDefaultAppOnRootOnce = true;
    this.router.navigateByUrl('/').then((): void => {
      this.router.navigateByUrl(app, {state: params});
    });
  }

  navigateTo(path: string, extras?: object): void {
    this.router.navigateByUrl(path || '', extras).catch(() => {
      // wait for routes to exist if navigation fails
      this.routesLoaded.pipe(take(1)).subscribe((): void => {
        this.router.navigateByUrl(path || '', extras);
      });
    });
  }

  // INTERIM ISPI-481001 — START: navigate via '/' first so that an identical target URL still produces a
  // fresh queryParams emission (needed so a repeated ISTA jump with the same FORK_CONTEXT_ID logs out again).
  // Delete once ISTA sends a user-id; the thin client then uses navigateTo again (user-id check is idempotent).
  navigateForcingRouteChange(url: string, extras?: object): void {
    this.suppressDefaultAppOnRootOnce = true;
    this.router.navigateByUrl('/').then((): void => {
      this.navigateTo(url, extras);
    });
  }
  // INTERIM ISPI-481001 — END


  private createNavigationRoutesForApps(): void {
    this.router.config = [];
    this.appsService.getVisibleApps().forEach((app: IIframeAppConfiguration | IWebComponentAppConfiguration): void => {
      this.router.config.push(this.routeFactory.getRouteForApp(app));
    });
    this.routesLoaded.next();
  }

  private openDefaultAppIfNoPathIsProvided(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd): void => {
        const url: string = event.url.substring(1).split('?')[0];
        if (url === '' || url === '/') {
          // INTERIM ISPI-481001 — START: skip default-app handling for the forced intermediate '/'
          // navigation triggered by navigateForcingRouteChange. Delete with navigateForcingRouteChange.
          if (this.suppressDefaultAppOnRootOnce) {
            this.suppressDefaultAppOnRootOnce = false;
            return;
          }
          // INTERIM ISPI-481001 — END
          this.navigateToPostLoginRedirectOrDefaultApp();
        }
      });
  }

  /**
   * Checks if a post-login redirect path was stored in localStorage (key: 'awp-post-login-redirect').
   * This is set by UserService.logOutWithRedirect() when a user-id mismatch triggers a logout,
   * so that the original deep link (including query params) is restored after the re-login.
   * If no redirect is stored, the user's default app is opened instead.
   */
  private navigateToPostLoginRedirectOrDefaultApp(): void {
    const postLoginRedirect: string = localStorage.getItem('awp-post-login-redirect');
    if (postLoginRedirect) {
      localStorage.removeItem('awp-post-login-redirect');
      console.log('[Webapp] [Navigation] Restoring post-login redirect: ' + postLoginRedirect);
      this.navigateTo(postLoginRedirect);
    } else {
      this.openDefaultApp();
    }
  }

  private openDefaultApp(): void {
    const user: UserData = this.userService.getCurrentUser();
    let userDefaultApp: IIframeAppConfiguration | IWebComponentAppConfiguration;
    userDefaultApp = this.appsService.getVisibleApps().find(app => app.titleResource === user?.getDefaultApp());
    if (userDefaultApp) {
      this.navigateTo(userDefaultApp.routingPath);
    } else {
      this.navigateTo('caseoverview');
    }
  }

  private setupNavigationAnalytics(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url: string = event.url.substring(1).split('?')[0];
        this.postNavigateToAnalyticsEvent(url, this.previousRoute);
        this.previousRoute = url;
      });
  }

  private setupVehicleReload(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd): void => {
        const url: string = event.url.substring(1).split('?')[0];
        if (url === 'vehicledetails') {
          this.serviceCaseManger.reloadAllServiceCases();
        }
      });
  }

  private postNavigateToAnalyticsEvent(path: string, origin: string): void {
    console.debug(`[Webapp] [Navigation] Navigated to ${path}.`);
    this.analyticsService.postNavigationEvent(path, origin);
  }
}
